import React from 'react'
import { H3 } from '@hub/heading'
import { Text } from '@hub/text'
import { Stack } from '@hub/stack'
import { Link } from '@hub/link'
import { CoreContainer } from '@hub/core-container'
import { Box } from '@hub/box'
import {
  EmailMediumIcon,
  LocationDropPinMediumIcon,
  PhoneMediumIcon,
} from '@hub/icon'
import { CentreDetails } from '@scentregroup/shared/helpers/centre'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import UtilityBoxOpeningHours from '@scentregroup/shared/components/utility-box-opening-hours'
import { parseCentreOrLinkedStoreHours } from '@scentregroup/shared/helpers/parse-opening-hours/parse-centre-or-linked-store-hours'
import locationLink from '@scentregroup/shared/helpers/location-link'
import formatTelephoneNumber from '@scentregroup/shared/helpers/telephone-number'
import {
  CloudinaryImage,
  IMAGE_SIZES_HERO,
} from '@scentregroup/shared/hub-components/cloudinary-image'

interface CentreFrontInfoProps {
  centre: CentreDetails
  tradingHours: TradingHourDay[]
}

export const CentreFrontInfo: React.FC<
  React.PropsWithChildren<CentreFrontInfoProps>
> = ({ centre, tradingHours }): JSX.Element => {
  const {
    title,
    timeZone,
    physicalAddress,
    suburb,
    state,
    postcode,
    latitude,
    longitude,
    telephoneNumber,
    country,
    emailAddress,
    slug,
    centreFrontImage,
  } = centre
  const address2 = `${suburb} ${state} ${postcode}`

  return (
    <CoreContainer>
      <Stack
        direction={['column', null, 'row']}
        gap="spacing-5"
        shouldWrapChildren={false}
        sx={{
          justifyContent: 'space-between',
        }}
        flexBasis={0}
      >
        <Stack
          sx={{
            maxWidth: [null, null, 640],
          }}
          gap="spacing-md"
          shouldWrapChildren={false}
        >
          <H3 as={'h1'} align={['center', null, 'left']}>
            Visit Westfield {title}
          </H3>
          <Box sx={{ maxWidth: 'size-3xl' }}>
            <UtilityBoxOpeningHours
              withinV2Design
              wrapperNoBorder
              headerNoPadding
              openingHours={parseCentreOrLinkedStoreHours(
                tradingHours,
                timeZone
              )}
              seeMoreOpeningHoursUrl={`/${slug}/opening-hours`}
            />
          </Box>
          <Stack direction="row" gap="spacing-md" shouldWrapChildren={false}>
            <Box sx={{ flex: 0 }}>
              <LocationDropPinMediumIcon boxSize="size-icon-md" />
            </Box>
            <Stack gap="spacing-sm">
              <Text>
                {physicalAddress}, {address2}
              </Text>
              {latitude && longitude && (
                <Link
                  href={locationLink(latitude, longitude)}
                  hasUnderline
                  isExternal
                  target="_blank"
                >
                  See on map
                </Link>
              )}
            </Stack>
          </Stack>
          {telephoneNumber && (
            <Stack direction="row" gap="spacing-md" shouldWrapChildren={false}>
              <Box sx={{ flex: 0 }}>
                <PhoneMediumIcon boxSize="size-icon-md" />
              </Box>
              <Stack
                direction={['column', 'row']}
                align={['start', 'center']}
                gap={'spacing-sm'}
              >
                <Link
                  href={`tel:${formatTelephoneNumber(
                    telephoneNumber,
                    country
                  )}`}
                  hasUnderline
                  isExternal
                  target="_blank"
                >
                  {telephoneNumber}
                </Link>
                <Text>Concierge</Text>
              </Stack>
            </Stack>
          )}
          <Stack direction="row" gap="spacing-md" shouldWrapChildren={false}>
            <Box sx={{ flex: 0 }}>
              <EmailMediumIcon boxSize="size-icon-md" />
            </Box>
            <Link
              href={`mailto:${emailAddress}`}
              hasUnderline
              isExternal
              target="_blank"
            >
              Email us
            </Link>
          </Stack>
        </Stack>
        {centreFrontImage?.url && (
          <Box
            sx={{
              maxWidth: [null, null, 'size-3xl'],
            }}
          >
            <CloudinaryImage
              ratio="3/2"
              imageSetOrImage={{
                type: 'WIDE',
                url: centreFrontImage.url,
                alt: `Westfield ${title} shopping centre`,
              }}
              sizes={IMAGE_SIZES_HERO}
            />
          </Box>
        )}
      </Stack>
    </CoreContainer>
  )
}
